"use client"

// packages/frontend/src/components/ui/reactbits/GlareHoverWrapper.tsx

import React, { type ReactNode } from "react"
import { motion, useMotionTemplate, useMotionValue } from "framer-motion"
import { useTheme } from "../../providers/ThemeProvider"
import { cn } from "@/utils/cn"

interface GlareHoverWrapperProps {
  children: ReactNode
  className?: string
  disabled?: boolean
}

/**
 * Wrapper for ReactBits GlareHover component with theme support
 * Provides enhanced hover effects with glare animation
 */
export function GlareHoverWrapper({
  children,
  className,
  disabled = false,
}: GlareHoverWrapperProps) {
  const { themeType } = useTheme()
  const isChildTheme = themeType === "child"

  // If disabled, return children without animation
  if (disabled) {
    return <>{children}</>
  }

  // Try to import ReactBits GlareHover, fallback to custom implementation
  let GlareHover: any = null
  try {
    const reactbits = require("@appletosolutions/reactbits")
    GlareHover = reactbits.GlareHover || reactbits.default?.GlareHover
  } catch (e) {
    // Fallback to custom implementation
  }

  // Custom glare effect using Framer Motion
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const glareOpacity = useMotionValue(0)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return

    const rect = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - rect.left)
    mouseY.set(e.clientY - rect.top)
    glareOpacity.set(1)
  }

  const handleMouseLeave = () => {
    glareOpacity.set(0)
  }

  const glareStyle = useMotionTemplate`radial-gradient(300px circle at ${mouseX}px ${mouseY}px, rgba(255,255,255,0.3), transparent 40%)`

  // If ReactBits component is available, use it
  if (GlareHover) {
    return (
      <GlareHover className={className} disabled={disabled}>
        {children}
      </GlareHover>
    )
  }

  // Custom implementation
  return (
    <motion.div
      className={cn("relative overflow-hidden", className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isChildTheme && (
        <motion.div
          className="pointer-events-none absolute inset-0 opacity-0"
          style={{
            background: glareStyle,
            opacity: glareOpacity,
          }}
          transition={{ duration: 0.3 }}
        />
      )}
    </motion.div>
  )
}

